/**
 * The status bar: where the caret is, what is playing, and how big the page is drawn.
 *
 * Transport lives here rather than in the menus because it is used constantly and a menu is
 * two clicks. The position counts from the count-in, which is why it can read as a negative
 * number of seconds: what is heard before the music starts is not part of the piece.
 *
 * The bar sticks to the bottom of the view, and the pager is on it: a page drawn large is
 * taller than the screen, and play, stop and the next page must not be at the end of it.
 */

import { crossStaff, doubling, measureIndexOf, voices } from "@app/score-editor";
import type { Score } from "@app/score-editor";
import type { Caret } from "@app/score-editor";
import type { ScoreActions } from "../lib/score-actions";
import type { ShownPage } from "../lib/use-shown-page";
import ScorePager from "./ScorePager";

/**
 * Which voice of how many the caret stands in, or `null` where the staff has only one.
 *
 * Silent on an undivided staff because that is almost every staff: a readout that said
 * “voice 1 of 1” everywhere would be noise, and the only time the number matters is when
 * there is more than one line to be standing in. An ossia is not one of the staff’s
 * voices and is not numbered among them, so standing in it says so in words.
 */
function voiceReading(score: Score, caret: Caret | null): string | null {
  if (caret === null) return null;
  const part = score.parts.find((candidate) => candidate.id === caret.partId);
  const measure = part?.measures[caret.measureId];
  if (measure === undefined) return null;
  const standing = voices.standingIn(measure, caret.voiceIndex);
  if (standing === null) return null;
  if (standing.ossia) return "in the ossia";
  const on = voices.voicesOn(measure, standing.staff).length;
  return on < 2 ? null : `voice ${standing.ordinal + 1} of ${on}`;
}

/**
 * Which staff the selection is drawn on, when that is not its voice's own.
 *
 * Silent otherwise, for the same reason the voice reading is: a note drawn where it
 * belongs is every note. A crossing is invisible in the document and easy to make by
 * accident with an arrow, so it says so while it is true.
 */
function crossReading(actions: ScoreActions): string | null {
  if (!crossStaff.isCrossed(actions.score, actions.selection)) return null;
  const staff = crossStaff.staffAt(actions.score, actions.selection);
  return staff === null ? null : `drawn on staff ${staff + 1}`;
}

/**
 * What the caret's player is holding, when it is not the instrument the part opened on.
 *
 * Silent the rest of the time, like the readings above it: a flute part playing the flute
 * says nothing worth saying. It matters while a doubling is in force, because the notes
 * around the caret are then written for an instrument whose name is nowhere on the screen.
 */
function holdingReading(score: Score, caret: Caret | null): string | null {
  if (caret === null) return null;
  const part = score.parts.find((candidate) => candidate.id === caret.partId);
  if (part === undefined) return null;
  const held = doubling.instrumentInHand(score, caret.partId, caret.measureId);
  if (held === null || held.name === part.instrument.name) return null;
  return `holding ${held.name.toLowerCase()}`;
}

/** Seconds as a clock, with a sign when the count-in is still running. */
function clock(seconds: number): string {
  const sign = seconds < 0 ? "−" : "";
  const whole = Math.floor(Math.abs(seconds));
  return `${sign}${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

const BUTTON =
  "h-8 rounded-[8px] border border-rule px-2.5 text-[12px] text-ink-faded hover:border-ink-quiet hover:text-ink";

const ON = "h-8 rounded-[8px] border border-ink bg-ink px-2.5 text-[12px] text-white";

export interface ScoreStatusProps {
  readonly actions: ScoreActions;
  /** Seconds into the piece, as of the last frame. */
  readonly position: number;
  /** How long the piece lasts, repeats followed. */
  readonly seconds: number;
  /** The page on the board, and turning it. */
  readonly sheet: ShownPage;
}

export default function ScoreStatus({
  actions,
  position,
  seconds,
  sheet,
}: ScoreStatusProps): JSX.Element {
  const { caret, keyboard, score, selection } = actions;
  const bar = caret === null ? null : measureIndexOf(score, caret.measureId) + 1;
  const voiced = voiceReading(score, caret);
  const across = crossReading(actions);
  const holding = holdingReading(score, caret);
  // A hidden staff cannot be clicked, so the page says while it is hidden: a part that
  // has silently gone is otherwise a bug the user goes looking for.
  const hiding = score.layout.hideEmptyStaves;
  // Attaching a keyboard and dropping it are the same button; choosing between several is
  // the Note menu's job, and this only says whether one is writing.
  const listening = keyboard.keyboards.find((device) => device.id === keyboard.listening) ?? null;

  return (
    <div className="sticky bottom-3 z-10 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-rule bg-white px-3 py-2 shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
      <button
        type="button"
        className={actions.loading ? `${BUTTON} animate-pulse` : BUTTON}
        onClick={actions.playPause}
        aria-busy={actions.loading}
        title={actions.loading ? "Loading the instruments this score is scored for" : undefined}
      >
        {actions.playing ? "❚❚ Pause" : "▶ Play"}
      </button>
      <button type="button" className={BUTTON} onClick={actions.stop}>
        ■ Stop
      </button>
      <span className="font-mono text-[11px] text-ink-faded">
        {clock(position)} / {clock(seconds)}
      </span>

      <button
        type="button"
        className={actions.metronome ? ON : BUTTON}
        onClick={actions.toggleMetronome}
        title="Sound a click on every beat"
      >
        Click
      </button>
      <button
        type="button"
        className={actions.loop === null ? BUTTON : ON}
        onClick={actions.toggleLoop}
        title="Repeat the selected passage"
      >
        Loop
      </button>
      <button
        type="button"
        className={actions.countIn ? ON : BUTTON}
        onClick={actions.toggleCountIn}
        title="Count a bar in before the music"
      >
        Count in
      </button>
      <button
        type="button"
        className={actions.concert ? ON : BUTTON}
        onClick={actions.toggleConcert}
        title="Read the score as it sounds rather than as its players read it"
      >
        Concert
      </button>

      <button
        type="button"
        className={listening === null ? BUTTON : ON}
        onClick={() => (listening === null ? keyboard.ask() : keyboard.listen(null))}
        title={
          listening === null
            ? "Play notes in from a MIDI keyboard"
            : `Writing from ${listening.name}. Click to stop.`
        }
      >
        Keyboard
      </button>

      <span className="ml-auto font-mono text-[11px] text-ink-quiet">
        {bar === null ? "no bar" : `bar ${bar}`}
        {voiced === null ? null : ` · ${voiced}`}
        {across === null ? null : ` · ${across}`}
        {holding === null ? null : ` · ${holding}`}
        {hiding ? " · silent staves hidden" : null} ·{" "}
        {selection.events.size === 0 ? "nothing selected" : `${selection.events.size} selected`}
      </span>

      <ScorePager page={sheet.page} count={sheet.count} onPage={sheet.turn} look="paper" />

      <span className="flex items-center gap-1">
        <button type="button" className={BUTTON} title="Smaller" onClick={() => actions.zoom(-6)}>
          −
        </button>
        <span className="w-11 text-center font-mono text-[11px] text-ink-faded">
          {actions.scale}%
        </span>
        <button type="button" className={BUTTON} title="Larger" onClick={() => actions.zoom(6)}>
          +
        </button>
      </span>
    </div>
  );
}
